import Text from "@/components/ui/text";
import { UserData } from "@/entities/users/types";
import React, { memo, useMemo, useState } from "react";
import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Legend,
  Tooltip as RechartsTooltip,
  TooltipProps
} from "recharts";
import {
  NameType,
  ValueType
} from "recharts/types/component/DefaultTooltipContent";
import { ChartDataType } from "@/entities/reporting/types";
import { LineAndBarChartCustomTooltip } from "@/components/graphs/WinRate/LineAndBarChartCustomTooltip";
import { cn } from "@/utils/utils";
import { isEmpty } from "lodash";

export type ChartWidgetType = "leadAnalysis" | "winRate";

export interface MouseMoveEvent {
  activeCoordinate?: {
    x: number;
    y: number;
  };
  activeTooltipIndex?: number;
  [key: string]: any;
}

interface Props {
  height: number;
  chartData: ChartDataType[];
  showComparison?: boolean;
  userData?: UserData;
  tooltipYpos?: number;
  chartWidgetType?: ChartWidgetType;
  isPercentage?: boolean;
  hideIndustry?: boolean;
  chartClassName?: string;
  legendClassName?: string;
}

const CustomBar = (props: any) => {
  const { x, y, width, height, fill } = props;
  const effectiveHeight = Math.max(height, 2);
  const adjustedY = y + (height - effectiveHeight);
  const cornerRadius = 3;
  return (
    <path
      d={`
        M ${x},${adjustedY + effectiveHeight}
        L ${x},${adjustedY + cornerRadius}
        Q ${x},${adjustedY} ${x + cornerRadius},${adjustedY}
        L ${x + width - cornerRadius},${adjustedY}
        Q ${x + width},${adjustedY} ${x + width},${adjustedY + cornerRadius}
        L ${x + width},${adjustedY + effectiveHeight}
        Z
      `}
      fill={fill}
    />
  );
};

const BarChart: React.FC<Props> = ({
  height,
  chartData,
  showComparison,
  userData,
  tooltipYpos = 10,
  chartWidgetType = "winRate",
  isPercentage = true,
  hideIndustry,
  chartClassName,
  legendClassName
}) => {
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  if (isEmpty(chartData)) return null;

  const handleMouseMove = (e: MouseMoveEvent) => {
    if (e && e.activeTooltipIndex !== undefined && e.activeCoordinate) {
      setTooltipPos({
        x: e.activeCoordinate.x - 50,
        y: tooltipYpos
      });
    }
  };

  const MemoizedComparisonTooltip = useMemo(() => {
    const ComparisonTooltip = ({
      active,
      payload
    }: TooltipProps<ValueType, NameType>) => {
      if (active && payload && payload.length) {
        const data = payload[0].payload as ChartDataType;
        const tooltipData = {
          ...data,
          youValue: Math.round(data.youValue),
          teamValue: Math.round(data.teamValue),
          organizationValue: Math.round(data.organizationValue),
          industryValue: Math.round(data.industryValue || 0)
        };
        return (
          <LineAndBarChartCustomTooltip
            data={tooltipData}
            userData={userData}
            showComparison={showComparison}
            chartWidgetType={chartWidgetType}
            hideIndustry={hideIndustry}
          />
        );
      }
      return null;
    };
    return memo(ComparisonTooltip);
  }, [userData, showComparison]);

  // Calculate bar width based on whether comparison is shown
  const barSize = showComparison ? 10 : 15;
  // Calculate bar gap if showing comparison
  const barGap = showComparison ? 2 : 0;

  return (
    <div className="flex w-full" style={{ height: `${height}px` }}>
      <ResponsiveContainer width="100%" height={height}>
        <RechartsBarChart
          data={chartData}
          margin={{ left: 25, right: 25, top: 5 }}
          onMouseMove={handleMouseMove}
          barGap={barGap}
          className={chartClassName}
        >
          <defs>
            <linearGradient id="you-gradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="30%" stopColor="var(--cta)" stopOpacity={0.8} />
              <stop offset="95%" stopColor="var(--cta)" stopOpacity={0.5} />
            </linearGradient>
            <linearGradient
              id="comparison-gradient"
              x1="0"
              y1="0"
              x2="0"
              y2="1"
            >
              <stop offset="30%" stopColor="var(--widget)" stopOpacity={0.8} />
              <stop offset="95%" stopColor="var(--widget)" stopOpacity={0.5} />
            </linearGradient>
          </defs>

          <XAxis
            dataKey="date"
            interval={0}
            tickLine={false}
            axisLine={{ stroke: "var(--widget)" }}
            tick={{ fill: "var(--secondary)", fontSize: 10 }}
          />
          <YAxis
            tickLine={false}
            axisLine={{ stroke: "var(--widget)" }}
            tick={{ fill: "var(--secondary)", fontSize: 12 }}
            tickFormatter={(tick) => `${isPercentage ? `${tick}%` : tick}`}
          />

          <Bar
            dataKey="youValue"
            shape={(props: any) => {
              const { x, y, width, height } = props;
              return (
                <CustomBar
                  x={x}
                  y={y}
                  width={width}
                  height={height}
                  fill="url(#you-gradient)"
                />
              );
            }}
            barSize={barSize}
          />

          {showComparison && (
            <>
              <Bar
                dataKey="teamValue"
                shape={(props: any) => {
                  const { x, y, width, height } = props;
                  return (
                    <CustomBar
                      x={x}
                      y={y}
                      width={width}
                      height={height}
                      fill="url(#comparison-gradient)"
                    />
                  );
                }}
                barSize={barSize}
              />
              <Bar
                dataKey="organizationValue"
                shape={(props: any) => {
                  const { x, y, width, height } = props;
                  return (
                    <CustomBar
                      x={x}
                      y={y}
                      width={width}
                      height={height}
                      fill="url(#comparison-gradient)"
                    />
                  );
                }}
                barSize={barSize}
              />
            </>
          )}

          <RechartsTooltip
            content={<MemoizedComparisonTooltip />}
            cursor={{ fill: "transparent" }}
            position={tooltipPos}
            wrapperStyle={{ visibility: tooltipPos.x ? "visible" : "hidden" }}
          />
          <Legend
            content={() => (
              <div
                className={cn(
                  "absolute -bottom-3 left-1/2 w-full",
                  legendClassName
                )}
              >
                <Text
                  as="div"
                  variant="smBodySecondary"
                >{`${chartData[0].date}-${chartData[chartData.length - 1].date}`}</Text>
              </div>
            )}
            verticalAlign="bottom"
            align="center"
          />
        </RechartsBarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default BarChart;
